import { rgba } from 'polished';
import { setGradient } from './utils.js';
import { pxToRem } from '../../../spacing/utils.js';

var brand = {
  '50': '#FFF5BA',
  '500': '#FFDE1C'
};
var brandPrimary = {
  '0': '#FFFFFF',
  '25': '#F8F8F9',
  '50': '#F3F3F5',
  '100': '#DBDBDF',
  '200': '#CACAD0',
  '300': '#B1B1BB',
  '400': '#A2A2AD',
  '500': '#8B8B99',
  '600': '#7E7E8B',
  '700': '#63636D',
  '800': '#4C4C54',
  '900': '#3A3A40',
  '1000': '#161618'
};
var gray = {
  '0': '#FFFFFF',
  '25': '#F8F8F9',
  '50': '#F3F3F5',
  '100': '#DBDBDF',
  '200': '#CACAD0',
  '300': '#B1B1BB',
  '400': '#A2A2AD',
  '500': '#8B8B99',
  '600': '#7E7E8B',
  '700': '#63636D',
  '800': '#4C4C54',
  '900': '#3A3A40',
  '1000': '#161618'
};
var blue = {
  '50': '#E9F1FE',
  '100': '#BCD2FB',
  '200': '#9CBCF9',
  '300': '#6E9EF6',
  '400': '#528BF4',
  '500': '#276EF1',
  '600': '#2364DB',
  '700': '#1C4EAB',
  '800': '#153D85',
  '900': '#102E65'
};
var indigo = {
  '50': '#EEEEFA',
  '100': '#CAC9EF',
  '200': '#B1AFE8',
  '300': '#8D8BDD',
  '400': '#7775D6',
  '500': '#5552CC',
  '600': '#4D4BBA',
  '700': '#3C3A91',
  '800': '#2F2D70',
  '900': '#242256'
};
var purple = {
  '50': '#F3F0F9',
  '100': '#D8D0ED',
  '200': '#C6B9E5',
  '300': '#AB98D9',
  '400': '#9B85D1',
  '500': '#8266C6',
  '600': '#765DB4',
  '700': '#5C488D',
  '800': '#48386D',
  '900': '#372B53'
};
var magenta = {
  '50': '#F8EDF5',
  '100': '#EBC8DF',
  '200': '#E1ADCF',
  '300': '#D387B9',
  '400': '#CA70AC',
  '500': '#BD4C97',
  '600': '#AC4589',
  '700': '#86366B',
  '800': '#682A53',
  '900': '#4F203F'
};
var red = {
  '50': '#FBECEA',
  '100': '#F3C3BE',
  '200': '#EDA69E',
  '300': '#E57D72',
  '400': '#E06456',
  '500': '#D83D2C',
  '600': '#C53828',
  '700': '#992B1F',
  '800': '#772218',
  '900': '#5B1A12'
};
var orange = {
  '50': '#FEF2ED',
  '100': '#FAD7C6',
  '200': '#F8C3AA',
  '300': '#F5A884',
  '400': '#F3976C',
  '500': '#F07D47',
  '600': '#DA7241',
  '700': '#AA5932',
  '800': '#844527',
  '900': '#65351E'
};
var brown = {
  '50': '#F7F4F1',
  '100': '#EBE5DE',
  '200': '#E0D6CA',
  '300': '#D3C5B4',
  '400': '#C3B19A',
  '500': '#B29A7D',
  '600': '#A38664',
  '700': '#826A4D',
  '800': '#6F5A42',
  '900': '#554532'
};
var yellow = {
  '50': '#FFF9EC',
  '100': '#FFEBC5',
  '200': '#FFE2A9',
  '300': '#FFD581',
  '400': '#FFCD69',
  '500': '#FFC043',
  '600': '#E8AF3D',
  '700': '#B58830',
  '800': '#8C6A25',
  '900': '#6B511C'
};
var green = {
  '50': '#EBF6F0',
  '100': '#C2E2D1',
  '200': '#A4D5BB',
  '300': '#7AC19C',
  '400': '#61B589',
  '500': '#39A36B',
  '600': '#349461',
  '700': '#28744C',
  '800': '#1F5A3B',
  '900': '#18442D'
};
var teal = {
  '50': '#E7F3F2',
  '100': '#B3D9D5',
  '200': '#8EC7C1',
  '300': '#5BADA5',
  '400': '#3B9D94',
  '500': '#0A8579',
  '600': '#09796E',
  '700': '#075E56',
  '800': '#064943',
  '900': '#043833'
};
var cyan = {
  '50': '#E9F2F4',
  '100': '#B9D8DE',
  '200': '#98C5CE',
  '300': '#68ABB7',
  '400': '#4B9AA9',
  '500': '#1E8194',
  '600': '#1B7587',
  '700': '#155C69',
  '800': '#114751',
  '900': '#0D363E'
};
var primary = {
  '0': gray['0'],
  '25': gray['25'],
  '50': gray['50'],
  '100': gray['100'],
  '200': gray['200'],
  '300': gray['300'],
  '400': gray['400'],
  '500': gray['500'],
  '600': gray['600'],
  '700': gray['700'],
  '800': gray['800'],
  '900': gray['900'],
  '1000': gray['1000']
};
var secondary = {
  '50': blue['50'],
  '100': blue['100'],
  '200': blue['200'],
  '300': blue['300'],
  '400': blue['400'],
  '500': blue['500'],
  '600': blue['600'],
  '700': blue['700'],
  '800': blue['800'],
  '900': blue['900']
};
var ai = {
  '50': setGradient(blue['50'], indigo['50']),
  '100': setGradient(blue['100'], indigo['100']),
  '200': setGradient(blue['200'], indigo['200']),
  '300': setGradient(blue['300'], indigo['300']),
  '400': setGradient(blue['400'], indigo['400']),
  '500': setGradient(blue['500'], indigo['500']),
  '600': setGradient(blue['600'], indigo['600']),
  '700': setGradient(blue['700'], indigo['700']),
  '800': setGradient(blue['800'], indigo['800']),
  '900': setGradient(blue['900'], indigo['900'])
};
var background = {
  '50': gray['0'],
  '75': gray['25'],
  '100': gray['50']
};
var text = {
  '100': gray['700'],
  '200': gray['800'],
  '300': gray['1000'],
  light: gray['0'],
  success: green['500'],
  danger: red['500'],
  ai: '' // defined in /modules/v2/foundation/theme/utils.ts
};
var border = {
  '100': gray['100'],
  '200': gray['500'],
  '300': gray['700']
};
var disabled = {
  '100': gray['100'],
  '200': gray['500'],
  '300': gray['700']
};
var assistant = {
  '50': purple['50'],
  '100': purple['100'],
  '200': purple['200'],
  '300': purple['300'],
  '400': purple['400'],
  '500': purple['500'],
  '600': purple['600'],
  '700': purple['700'],
  '800': purple['800'],
  '900': purple['900']
};
var focus = {
  primary: primary['200'],
  secondary: secondary['100'],
  success: green['200'],
  danger: red['200'],
  warning: yellow['200'],
  info: blue['200'],
  assistant: assistant['100'],
  ai: ai['200']
};
var lightSpectrum = {
  gray: gray,
  blue: blue,
  indigo: indigo,
  purple: purple,
  magenta: magenta,
  red: red,
  orange: orange,
  brown: brown,
  yellow: yellow,
  green: green,
  teal: teal,
  cyan: cyan
};
var lightThemeColors = {
  brand: brand,
  brandPrimary: brandPrimary,
  primary: primary,
  secondary: secondary,
  assistant: assistant,
  background: background,
  text: text,
  border: border,
  disabled: disabled,
  focus: focus,
  ai: ai
};
var shadowToken = lightSpectrum.gray[1000];
var lightDropShadowTokens = {
  '100': "0 " + pxToRem(2) + " " + pxToRem(2) + " " + rgba(shadowToken, 0.04) + ", 0 0 " + pxToRem(2) + " " + rgba(shadowToken, 0.04),
  '200': "0 " + pxToRem(1) + " " + pxToRem(2) + " " + rgba(shadowToken, 0.3) + ", 0 " + pxToRem(2) + " " + pxToRem(6) + " " + rgba(shadowToken, 0.15),
  '300': "0 " + pxToRem(8) + " " + pxToRem(20) + " " + rgba(shadowToken, 0.08) + ", 0 " + pxToRem(4) + " " + pxToRem(18) + " " + rgba(shadowToken, 0.04),
  '400': "0 " + pxToRem(6) + " " + pxToRem(18) + " " + rgba(shadowToken, 0.12) + ", 0 " + pxToRem(14) + " " + pxToRem(20) + " " + rgba(shadowToken, 0.16)
};
var lightColorTokens = Object.assign(Object.assign(Object.assign({}, lightSpectrum), lightThemeColors), {
  lightDropShadowTokens: lightDropShadowTokens
});

export { lightColorTokens, lightDropShadowTokens, lightSpectrum, lightThemeColors };
